/**
 * CooperScenes Landing Page
 * Displays scene cards that link to individual viewer pages
 */

import { config, environment } from './config.js';
import { applyBranding } from './utils/branding.js';
import { formatSceneTitle } from './utils/sceneTitle.js';
import { countDownload, renderDownloadTotal } from './utils/downloadCounter.js';

// Every download link, on this page or a scene page, counts toward one of
// the same three counters the CooperScene project page uses. Config items
// are per-split (mini/train/validate/test/mcap); train/validate/test (and
// their per-scene chunks) are all slices of the one 'full-dataset' total.
const DOWNLOAD_COUNTER_KEYS = ['mini-set', 'full-dataset', 'mcap'];

function counterKeyFor(itemId) {
  if (itemId === 'mini') return 'mini-set';
  if (itemId === 'mcap') return 'mcap';
  return 'full-dataset';
}

/**
 * Application state
 */
const state = {
  scenes: [],
  downloads: [],
  loading: true
};

/**
 * Initialize the landing page
 */
async function init() {
  console.log(`Multi-vehicle Sensing Data landing page initializing in ${environment} mode...`);

  // Apply branding configuration
  applyBranding(config);

  // Load scene configuration from config.json
  await loadSceneConfig();

  // Render scene cards
  renderSceneCards();

  // Render dataset download cards
  renderDownloads();

  console.log('Multi-vehicle Sensing Data landing page initialized successfully');
}

/**
 * Load scene configuration from config.json
 */
async function loadSceneConfig() {
  try {
    const response = await fetch('./config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status}`);
    }

    const data = await response.json();
    state.scenes = data.rrdFiles || [];
    state.downloads = data.downloads || [];
    state.loading = false;

    console.log(`Loaded ${state.scenes.length} scenes from config.json`);
  } catch (error) {
    console.error('Error loading scene configuration:', error);
    showError('Failed to load scene configuration. Please try again later.');
    state.loading = false;
  }
}

/**
 * Render dataset download cards from config.json's `downloads` array.
 * Clicks are counted (best-effort, never blocking) via the same Abacus
 * counters the CooperScene project site uses, and the section shows the
 * same combined "X downloads" total.
 */
function renderDownloads() {
  const section = document.getElementById('downloads-section');
  const grid = document.getElementById('download-cards-grid');
  if (!section || !grid || state.downloads.length === 0) return;

  for (const item of state.downloads) {
    const card = document.createElement('div');
    card.className = 'download-card';

    const title = document.createElement('h3');
    title.className = 'download-card-title';
    title.textContent = item.title;

    const size = document.createElement('span');
    size.className = 'download-card-size';
    size.textContent = item.size;
    title.appendChild(size);

    const btn = document.createElement('a');
    btn.className = 'download-btn';
    btn.href = item.url;
    btn.textContent = 'Download';
    btn.addEventListener('click', () => countDownload(counterKeyFor(item.id)));

    card.append(title, btn);
    grid.appendChild(card);
  }

  section.hidden = false;
  renderDownloadTotal(document.querySelector('[data-download-total]'), DOWNLOAD_COUNTER_KEYS);
}

/**
 * Render scene cards to the grid
 */
function renderSceneCards() {
  const grid = document.getElementById('scene-cards-grid');

  if (!grid) {
    console.error('Scene cards grid container not found');
    return;
  }

  // Clear loading indicator
  grid.innerHTML = '';

  if (state.scenes.length === 0) {
    grid.innerHTML = `
      <div class="error-message" style="grid-column: 1/-1; text-align: center; padding: 3rem;">
        <p style="font-size: 1.5rem; color: var(--text-secondary);">No scenes available at this time.</p>
      </div>
    `;
    return;
  }

  // Create a card for each scene
  for (const scene of state.scenes) {
    const card = createSceneCard(scene);
    grid.appendChild(card);
  }
}

/**
 * Create a scene card element: a clickable thumbnail, with the title
 * and its View/Download buttons on one row underneath.
 */
function createSceneCard(scene) {
  const card = document.createElement('div');
  card.className = 'scene-card';
  card.id = `card-${scene.id}`;

  const displayTitle = formatSceneTitle(scene.title);
  const viewerUrl = `./viewer.html?scene=${scene.id}`;
  const thumbnailHtml = scene.thumbnail
    ? `<img src="${scene.thumbnail}" alt="${displayTitle} thumbnail" />`
    : `<span>Preview Image</span>`;
  const downloadHtml = scene.sceneZipUrl
    ? `<a class="scene-btn scene-btn-download" href="${scene.sceneZipUrl}" data-scene="${scene.id}">Download</a>`
    : '';

  card.innerHTML = `
    <a class="scene-card-link" href="${viewerUrl}">
      <div class="scene-card-thumbnail">
        ${thumbnailHtml}
      </div>
    </a>
    <div class="scene-card-footer">
      <h3 class="scene-card-title">${displayTitle}</h3>
      <div class="scene-card-actions">
        <a class="scene-btn scene-btn-view" href="${viewerUrl}">View</a>
        ${downloadHtml}
      </div>
    </div>
  `;

  const dl = card.querySelector('.scene-btn-download');
  if (dl) {
    // Per-scene zips are chunks of the full dataset.
    dl.addEventListener('click', () => countDownload('full-dataset'));
  }

  return card;
}

/**
 * Show error message
 */
function showError(message) {
  const grid = document.getElementById('scene-cards-grid');
  if (grid) {
    grid.innerHTML = `
      <div class="error-message" style="grid-column: 1/-1; text-align: center; padding: 3rem;">
        <p style="font-size: 1.5rem; color: var(--error-color); margin-bottom: 1rem;">⚠️ Error</p>
        <p style="color: var(--text-secondary);">${message}</p>
      </div>
    `;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for debugging
window.cooperScenes = {
  state,
  loadSceneConfig,
  renderSceneCards
};
